import { createContext, useContext, useState, type ReactNode } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// Help context — app-wide help toggle.
//
// Default OFF: help cards + grey explainer text are hidden until the user
// clicks the floating HelpFab. State persists across sessions via localStorage.
// try/catch on every storage call for SSR / private-mode safety.
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'ggm_help_open';

type HelpContextValue = {
  helpOpen: boolean;
  toggleHelp: () => void;
  setHelpOpen: (v: boolean) => void;
};

const HelpContext = createContext<HelpContextValue | null>(null);

export function HelpProvider({ children }: { children: ReactNode }) {
  const [helpOpen, setHelpOpenState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  const setHelpOpen = (v: boolean) => {
    try {
      localStorage.setItem(STORAGE_KEY, v ? '1' : '0');
    } catch { /* ignore */ }
    setHelpOpenState(v);
  };

  const toggleHelp = () => setHelpOpen(!helpOpen);

  return (
    <HelpContext.Provider value={{ helpOpen, toggleHelp, setHelpOpen }}>
      {children}
    </HelpContext.Provider>
  );
}

export function useHelp(): HelpContextValue {
  const ctx = useContext(HelpContext);
  if (!ctx) throw new Error('useHelp must be called inside HelpProvider');
  return ctx;
}
